import React, { useEffect, useState } from 'react';
import API from '../api';
import { IcKey, IcPlus, IcTrash, IcRefresh, IcCopy, IcShield, IcUser, IcUsers, IcCheck } from './Icons';

const SCHOOL_ID = 1;

export default function ApiKeys() {
  const [keys, setKeys] = useState([]);
  const [students, setStudents] = useState([]);
  const [tab, setTab] = useState('school');
  const [form, setForm] = useState({ label: '', student_id: '' });
  const [copied, setCopied] = useState(null);
  const [msg, setMsg] = useState('');

  const load = () => {
    API.get(`/keys/school/${SCHOOL_ID}`).then(r => setKeys(r.data)).catch(() => {});
    API.get(`/students/school/${SCHOOL_ID}`).then(r => setStudents(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const schoolKeys = keys.filter(k => k.role === 'school');
  const parentKeys = keys.filter(k => k.role === 'parent');

  const copy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const generate = async () => {
    setMsg('');
    try {
      if (tab === 'school') {
        await API.post(`/keys/school/${SCHOOL_ID}?label=${encodeURIComponent(form.label || 'Clé école')}`);
      } else {
        if (!form.student_id) return setMsg('Sélectionnez un élève');
        const st = students.find(s => s.id === +form.student_id);
        const label = form.label || (st ? `${st.first_name} ${st.last_name} — parent` : 'Parent');
        await API.post(`/keys/parent/${SCHOOL_ID}/student/${form.student_id}?label=${encodeURIComponent(label)}`);
      }
      setForm({ label: '', student_id: '' });
      load();
    } catch (e) { setMsg(e.response?.data?.detail || 'Erreur'); }
  };

  const revoke = async (id) => {
    if (!window.confirm('Révoquer cette clé ?')) return;
    await API.delete(`/keys/${id}`);
    load();
  };

  const restore = async (id) => {
    await API.post(`/keys/${id}/restore`);
    load();
  };

  const ParentKeyRow = ({ k }) => {
    const student = students.find(s => s.id === k.student_id);
    const pinDisplay = k.pin ? `${k.pin.slice(0,3)} ${k.pin.slice(3)}` : '— —';
    return (
      <div style={{
        borderRadius: 12, marginBottom: 10, overflow: 'hidden',
        border: `1px solid ${k.active ? 'var(--emerald)' : 'rgba(239,68,68,0.2)'}`,
        opacity: k.active ? 1 : 0.6,
      }}>
        {/* Header vert avec le PIN */}
        <div style={{ background: k.active ? 'var(--emerald)' : '#94A3B8', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.75)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 2 }}>
              Code de connexion parent
            </div>
            <div style={{ fontSize: 32, fontWeight: 800, color: '#fff', letterSpacing: '0.18em', fontVariantNumeric: 'tabular-nums' }}>
              {pinDisplay}
            </div>
          </div>
          <button
            onClick={() => copy(k.pin, `pin-${k.id}`)}
            style={{ background: 'rgba(255,255,255,0.25)', border: 'none', borderRadius: 8, padding: '7px 12px', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            {copied === `pin-${k.id}` ? <IcCheck size={13} stroke="#fff" strokeWidth={2.5} /> : <IcCopy size={13} stroke="#fff" strokeWidth={2} />}
            Copier
          </button>
        </div>
        {/* Détails */}
        <div style={{ padding: '10px 16px', background: 'var(--slate-50)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--slate-800)' }}>
              {k.label}
              {student && <span style={{ fontWeight: 400, color: 'var(--slate-500)', marginLeft: 6 }}>
                · {student.first_name} {student.last_name} ({student.class_name})
              </span>}
            </div>
            <div style={{ fontSize: 11, color: 'var(--slate-300)', marginTop: 2 }}>
              Créée le {new Date(k.created_at).toLocaleDateString('fr-FR')}
              {k.last_used && ` · Utilisée le ${new Date(k.last_used).toLocaleDateString('fr-FR')}`}
            </div>
          </div>
          {k.active
            ? <button className="btn btn-danger btn-sm btn-icon" onClick={() => revoke(k.id)} title="Révoquer"><IcTrash size={14} strokeWidth={2} /></button>
            : <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => restore(k.id)}>Réactiver</button>}
        </div>
      </div>
    );
  };

  const SchoolKeyRow = ({ k }) => (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14,
      padding: '12px 16px', borderRadius: 10,
      background: k.active ? 'var(--slate-50)' : 'rgba(239,68,68,0.04)',
      border: `1px solid ${k.active ? 'var(--slate-200)' : 'rgba(239,68,68,0.2)'}`,
      marginBottom: 8, opacity: k.active ? 1 : 0.7,
    }}>
      <div style={{ width: 36, height: 36, borderRadius: 9, flexShrink: 0, background: 'var(--indigo-l)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <IcShield size={16} stroke="var(--indigo)" strokeWidth={2} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--slate-800)', marginBottom: 2 }}>{k.label}</div>
        <div style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--slate-400)', letterSpacing: '0.04em', wordBreak: 'break-all' }}>{k.key}</div>
        <div style={{ fontSize: 11, color: 'var(--slate-300)', marginTop: 2 }}>
          Créée le {new Date(k.created_at).toLocaleDateString('fr-FR')}
          {k.last_used && ` · Utilisée le ${new Date(k.last_used).toLocaleDateString('fr-FR')}`}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        <button className="btn btn-ghost btn-sm btn-icon" onClick={() => copy(k.key, k.id)} title="Copier la clé">
          {copied === k.id ? <IcCheck size={14} stroke="var(--emerald)" strokeWidth={2.5} /> : <IcCopy size={14} strokeWidth={1.5} />}
        </button>
        {k.active
          ? <button className="btn btn-danger btn-sm btn-icon" onClick={() => revoke(k.id)} title="Révoquer"><IcTrash size={14} strokeWidth={2} /></button>
          : <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => restore(k.id)}>Réactiver</button>}
      </div>
    </div>
  );

  return (
    <div style={{ padding: 28 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 className="page-title">Clés API</h1>
        <p className="page-subtitle">Gérez les accès des écoles et des parents</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 20, alignItems: 'start' }}>

        {/* Liste des clés */}
        <div>
          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
            {[
              { label: 'Clés école', count: schoolKeys.filter(k=>k.active).length, Icon: IcShield, color: 'var(--indigo)', bg: 'var(--indigo-l)' },
              { label: 'Clés parent', count: parentKeys.filter(k=>k.active).length, Icon: IcUsers, color: 'var(--emerald)', bg: 'var(--emerald-l)' },
            ].map(({ label, count, Icon, color, bg }) => (
              <div key={label} className="card" style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon size={18} stroke={color} strokeWidth={2} />
                </div>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--slate-800)' }}>{count}</div>
                  <div style={{ fontSize: 12, color: 'var(--slate-400)' }}>{label} actives</div>
                </div>
              </div>
            ))}
          </div>

          {/* Clés école */}
          <div className="card" style={{ padding: 20, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <IcShield size={15} stroke="var(--indigo)" strokeWidth={2} />
              <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--slate-800)' }}>Clés École</span>
              <span className="badge badge-indigo" style={{ marginLeft: 'auto' }}>{schoolKeys.length}</span>
            </div>
            {schoolKeys.length === 0
              ? <div className="empty" style={{ padding: '24px 0' }}><p>Aucune clé école générée</p></div>
              : schoolKeys.map(k => <SchoolKeyRow key={k.id} k={k} />)}
          </div>

          {/* Clés parent */}
          <div className="card" style={{ padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <IcUser size={15} stroke="var(--emerald)" strokeWidth={2} />
              <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--slate-800)' }}>Clés Parent</span>
              <span className="badge badge-green" style={{ marginLeft: 'auto' }}>{parentKeys.length}</span>
            </div>
            {parentKeys.length === 0
              ? <div className="empty" style={{ padding: '24px 0' }}><p>Aucune clé parent générée</p></div>
              : parentKeys.map(k => <ParentKeyRow key={k.id} k={k} />)}
          </div>
        </div>

        {/* Formulaire génération */}
        <div className="card" style={{ padding: 24, position: 'sticky', top: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
            <IcKey size={16} stroke="var(--indigo)" strokeWidth={2} />
            <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--slate-800)' }}>Nouvelle clé</span>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 18, background: 'var(--slate-100)', borderRadius: 8, padding: 4 }}>
            {[{ id: 'school', label: 'École' }, { id: 'parent', label: 'Parent' }].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} style={{
                flex: 1, padding: '7px', border: 'none', borderRadius: 6,
                background: tab === t.id ? '#fff' : 'transparent',
                color: tab === t.id ? 'var(--slate-800)' : 'var(--slate-400)',
                fontWeight: tab === t.id ? 600 : 400, fontSize: 13, cursor: 'pointer',
                boxShadow: tab === t.id ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                transition: 'all 0.15s',
              }}>{t.label}</button>
            ))}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
            <div>
              <label className="label">Libellé</label>
              <input className="input" placeholder={tab === 'school' ? 'Clé école principale' : 'Nom du parent'}
                value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} />
            </div>

            {tab === 'parent' && (
              <div>
                <label className="label">Élève concerné</label>
                <select className="input" value={form.student_id} onChange={e => setForm(f => ({ ...f, student_id: e.target.value }))}>
                  <option value="">Sélectionner un élève...</option>
                  {students.map(s => (
                    <option key={s.id} value={s.id}>{s.first_name} {s.last_name} — {s.class_name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {msg && <div style={{ padding: '8px 12px', background: 'var(--red-l)', color: 'var(--red)', borderRadius: 7, fontSize: 12, marginBottom: 12 }}>{msg}</div>}

          <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={generate}>
            <IcPlus size={15} strokeWidth={2.5} />
            Générer la clé
          </button>

          <div style={{ marginTop: 20, padding: 14, background: 'var(--slate-50)', borderRadius: 10, border: '1px solid var(--slate-200)' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--slate-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Format des clés</div>
            <div style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--slate-400)', lineHeight: 1.8 }}>
              <div>École : <span style={{ color: 'var(--indigo)' }}>CDC-SCH-XXXXXXXX-XXXXXXXX-XXXXXXXX</span></div>
              <div>Parent : <span style={{ color: 'var(--emerald)' }}>CDC-PAR-XXXXXXXX-XXXXXXXX-XXXXXXXX</span></div>
            </div>
          </div>

          <div style={{ marginTop: 14, padding: 14, background: 'rgba(16,185,129,0.06)', borderRadius: 10, border: '1px solid rgba(16,185,129,0.2)' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--emerald)', marginBottom: 6 }}>Comment partager avec les parents</div>
            <div style={{ fontSize: 11, color: 'var(--slate-500)', lineHeight: 1.7 }}>
              1. Générez une clé parent pour l'élève<br/>
              2. Un <strong>code à 6 chiffres</strong> s'affiche<br/>
              3. Envoyez-le par WhatsApp ou SMS<br/>
              4. Le parent tape ce code dans l'appli — c'est tout
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
